import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { MapType } from '../../constants/settings'
import { theme } from '../ui/GlobalStyle'

const Z_INDEX = {
  SAVED: 2,
  DEFAULT: 1,
}

const getDisplayLabel = (typesAccess, id) => {
  const type = typesAccess.getType(id)
  if (!type) {
    return null
  }

  if (type.cultivar) {
    const parentType = typesAccess.getParentType(id)
    if (
      parentType &&
      parentType.commonName &&
      parentType.scientificName &&
      (!type.commonName ||
        type.commonName.toLowerCase() === parentType.commonName.toLowerCase())
    ) {
      return {
        text: `${parentType.commonName} '${type.cultivar}'`,
        isScientific: false,
        typeId: id,
      }
    }
  }

  if (type.commonName) {
    return {
      text: type.commonName,
      isScientific: false,
      typeId: id,
    }
  }

  if (type.scientificName) {
    return {
      text: type.scientificName,
      isScientific: true,
      typeId: id,
    }
  }

  return null
}

const escapeHtml = (text) => {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

const formatLabelHtml = (labelData, selectedTypes) =>
  labelData
    .map((item) => {
      const escapedText = escapeHtml(item.text)
      const content = item.isScientific ? `<i>${escapedText}</i>` : escapedText
      const isSelected = selectedTypes.includes(item.typeId)
      const opacity = isSelected ? '1.0' : '0.5'
      return `<span data-type-id="${item.typeId}" style="opacity: ${opacity}">${content}</span>`
    })
    .join('<br>')

const createTextShadow = (color, size = 1, times = 1) => {
  const shadows = []
  for (let i = 0; i < times; i++) {
    shadows.push(`0px 0px ${size}px ${color}`)
  }
  return shadows.join(', ')
}

const getLabelStyleConfig = (mapType) => {
  const configs = {
    [MapType.Hybrid]: {
      fontWeight: 500,
      color: theme.background,
      textShadow: createTextShadow(theme.headerText, 2, 5),
      backgroundColor: 'unset',
    },
  }

  return (
    configs[mapType] || {
      fontWeight: 500,
      color: theme.secondaryText,
      textShadow: createTextShadow(theme.background, 2, 10),
      backgroundColor: 'unset',
    }
  )
}

const setLabelTextStyle = (div, mapType) => {
  const config = getLabelStyleConfig(mapType)
  Object.keys(config).forEach((key) => {
    div.style[key] = config[key]
  })
}

const createBaseLabelDiv = (isSaved) => {
  const div = document.createElement('div')
  div.style.position = 'absolute'
  div.style.padding = '4px 8px'
  div.style.fontSize = '12px'
  div.style.pointerEvents = 'none'
  div.style.marginTop = '5px'
  div.style.textAlign = 'center'
  div.style.display = 'block'
  div.style.zIndex = isSaved ? Z_INDEX.SAVED : Z_INDEX.DEFAULT
  return div
}

const createLabel = (
  google,
  googleMap,
  location,
  labelHtml,
  isHovered,
  mapType,
  isSaved,
) => {
  const label = new google.OverlayView()
  label.position = new google.LatLng(location.lat, location.lng)
  label.labelHtml = labelHtml
  label.locationId = location.id
  label.overlayLayerPane = null
  label.overlayMouseTargetPane = null
  label.isHovered = isHovered
  label.mapType = mapType
  label.isSaved = isSaved

  label.onAdd = function () {
    const div = createBaseLabelDiv(this.isSaved)
    setLabelTextStyle(div, this.mapType)
    div.innerHTML = this.labelHtml

    this.div = div
    const panes = this.getPanes()
    this.overlayLayerPane = panes.overlayLayer
    this.overlayMouseTargetPane = panes.overlayMouseTarget
    const targetPane = this.isHovered
      ? this.overlayMouseTargetPane
      : this.overlayLayerPane
    targetPane.appendChild(div)
  }

  label.draw = function () {
    const projection = this.getProjection()
    const position = projection.fromLatLngToDivPixel(this.position)

    const div = this.div
    div.style.left = `${position.x}px`
    div.style.top = `${position.y}px`
    div.style.transform = 'translate(-50%, 0)'
  }

  label.onRemove = function () {
    if (this.div) {
      this.div.parentNode.removeChild(this.div)
      this.div = null
    }
  }

  label.moveToPane = function (isHovered) {
    if (!this.div || !this.overlayLayerPane || !this.overlayMouseTargetPane) {
      return
    }
    const targetPane = isHovered
      ? this.overlayMouseTargetPane
      : this.overlayLayerPane
    if (this.div.parentNode !== targetPane) {
      targetPane.appendChild(this.div)
    }
  }

  label.updateStyle = function (mapType) {
    if (!this.div) {
      return
    }
    this.mapType = mapType
    setLabelTextStyle(this.div, mapType)
  }

  label.updatePosition = function (google, lat, lng) {
    this.position = new google.LatLng(lat, lng)
    this.draw()
  }

  label.updateHtml = function (newHtml) {
    if (!this.div || this.labelHtml === newHtml) {
      return
    }
    this.labelHtml = newHtml
    this.div.innerHTML = newHtml
  }

  label.setMap(googleMap)
  return label
}

const getMarkerIcon = (google, isSaved) => ({
  url: isSaved ? '/saved_location_dot.svg' : '/location_blue_dot.svg',
  anchor: new google.Point(8, 8),
  scaledSize: new google.Size(16, 16),
})

const LocationMarkers = ({
  locations,
  googleMap,
  getGoogleMaps,
  onLocationClick,
  showLabels,
}) => {
  const markersRef = useRef(new Map())
  const [hoveredLocationId, setHoveredLocationId] = useState(null)
  const typesAccess = useSelector((state) => state.type.typesAccess)
  const { types: selectedTypes } = useSelector((state) => state.filter)
  const { mapType } = useSelector((state) => state.settings)

  useEffect(() => {
    if (!googleMap || !getGoogleMaps) {
      return
    }

    const google = getGoogleMaps()
    const currentMarkers = markersRef.current

    const newLocationIds = new Set(locations.map((loc) => loc.id))
    const existingLocationIds = new Set(currentMarkers.keys())

    existingLocationIds.forEach((locationId) => {
      if (!newLocationIds.has(locationId)) {
        const markerData = currentMarkers.get(locationId)
        if (markerData) {
          markerData.marker.setMap(null)
          if (markerData.label) {
            markerData.label.setMap(null)
          }
          google.event.clearInstanceListeners(markerData.marker)
          currentMarkers.delete(locationId)
        }
      }
    })

    locations.forEach((location) => {
      const isSaved = Boolean(location.in_list)

      if (!existingLocationIds.has(location.id)) {
        const labelData = (location.type_ids || [])
          .map((id) => getDisplayLabel(typesAccess, id))
          .filter(Boolean)

        const marker = new google.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: googleMap,
          optimized: locations.length > 100,
          icon: getMarkerIcon(google, isSaved),
          zIndex: isSaved ? Z_INDEX.SAVED : Z_INDEX.DEFAULT,
        })

        google.event.addListener(marker, 'mouseover', () => {
          setHoveredLocationId(location.id)
        })

        google.event.addListener(marker, 'mouseout', () => {
          setHoveredLocationId(null)
        })

        if (onLocationClick) {
          google.event.addListener(marker, 'click', (event) => {
            event.stop()
            onLocationClick(location)
          })
        }

        currentMarkers.set(location.id, {
          marker,
          label: null,
          labelData,
          location,
          isSaved,
        })
      } else {
        const markerData = currentMarkers.get(location.id)
        const prevLocation = markerData.location
        if (
          prevLocation.lat !== location.lat ||
          prevLocation.lng !== location.lng
        ) {
          markerData.marker.setPosition({
            lat: location.lat,
            lng: location.lng,
          })
          if (markerData.label) {
            markerData.label.updatePosition(google, location.lat, location.lng)
          }
          markerData.location = location
        }

        // Update marker icon and zIndex if saved state changed
        if (markerData.isSaved !== isSaved) {
          markerData.marker.setIcon(getMarkerIcon(google, isSaved))
          markerData.marker.setZIndex(isSaved ? Z_INDEX.SAVED : Z_INDEX.DEFAULT)
          markerData.isSaved = isSaved
          // Update label z-index if it exists
          if (markerData.label && markerData.label.div) {
            markerData.label.div.style.zIndex = isSaved
              ? Z_INDEX.SAVED
              : Z_INDEX.DEFAULT
            markerData.label.isSaved = isSaved
          }
        }

        const newLabelData = (location.type_ids || [])
          .map((id) => getDisplayLabel(typesAccess, id))
          .filter(Boolean)
        markerData.labelData = newLabelData
      }
    })

    currentMarkers.forEach((markerData, locationId) => {
      const isHovered = hoveredLocationId === locationId
      const shouldShowLabel = showLabels || isHovered

      if (
        shouldShowLabel &&
        !markerData.label &&
        markerData.labelData.length > 0
      ) {
        const labelHtml = formatLabelHtml(markerData.labelData, selectedTypes)
        markerData.label = createLabel(
          google,
          googleMap,
          markerData.location,
          labelHtml,
          isHovered,
          mapType,
          markerData.isSaved,
        )
      }

      if (!shouldShowLabel && markerData.label) {
        markerData.label.setMap(null)
        markerData.label = null
      }

      if (markerData.label && markerData.label.div) {
        if (markerData.label.moveToPane) {
          markerData.label.moveToPane(isHovered)
        }

        if (markerData.label.updateStyle) {
          markerData.label.updateStyle(mapType)
        }

        const newLabelHtml = formatLabelHtml(
          markerData.labelData,
          selectedTypes,
        )
        if (markerData.label.updateHtml) {
          markerData.label.updateHtml(newLabelHtml)
        }

        const spans =
          markerData.label.div.querySelectorAll('span[data-type-id]')
        spans.forEach((span) => {
          const typeId = parseInt(span.getAttribute('data-type-id'), 10)
          const isSelected = selectedTypes.includes(typeId)
          span.style.opacity = isSelected ? '1.0' : '0.5'
        })
      }
    })
  }, [
    locations,
    googleMap,
    getGoogleMaps,
    onLocationClick,
    typesAccess,
    selectedTypes,
    showLabels,
    hoveredLocationId,
    mapType,
  ])

  useEffect(
    () => () => {
      const google = getGoogleMaps?.()
      if (!google) {
        return
      }

      markersRef.current.forEach((markerData) => {
        markerData.marker.setMap(null)
        if (markerData.label) {
          markerData.label.setMap(null)
        }
        google.event.clearInstanceListeners(markerData.marker)
      })

      markersRef.current.clear()
    },
    [getGoogleMaps],
  )

  return null
}

export default LocationMarkers
